import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/700.css';
import '@fontsource/sintony/400.css';
import '@fontsource/sintony/700.css';
import { all } from '@awesome.me/kit-f45c63b75c/icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { createTheme, ThemeProvider } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import React, { useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter } from 'react-router';
import { ToastContainer } from 'react-toastify';

import GlobalTheme from './themes/GlobalTheme';
import defaultConfig from './config';
import configureI18n from './i18n';
import { AuthContext } from './state/Context';
import { Routing } from './routing';
import { ErrorPage } from './pages';

// Use duration plugin
dayjs.extend(duration);

// Use Font Awesome icons via global use
library.add(all);

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      retry: false,
      throwOnError: true,
    },
    mutations: {
      retry: false,
      throwOnError: true,
    },
  },
});

/**
 * Component for mounting the Digital Twin application.
 * @returns {import('react').FunctionComponent} React element that contains the application.
 */
function App() {
  const config = defaultConfig;
  const {
    app: { debug },
    translations,
  } = config;

  configureI18n(translations, debug);

  const [tokens, setTokens] = useState({});
  const memoizedTokens = useMemo(
    () => ({
      tokens,
      setTokens,
    }),
    [tokens],
  );

  return (
    <main style={{ height: '100%' }}>
      <BrowserRouter>
        <ThemeProvider theme={createTheme(GlobalTheme)}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <QueryClientProvider client={queryClient}>
              <ErrorBoundary FallbackComponent={ErrorPage}>
                <AuthContext.Provider value={memoizedTokens}>
                  <Routing />
                  <ToastContainer
                    position="top-center"
                    autoClose={false}
                    closeButton={false}
                    closeOnClick={false}
                  />
                </AuthContext.Provider>
              </ErrorBoundary>
            </QueryClientProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </BrowserRouter>
    </main>
  );
}

export default App;
